import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clipboard,
  KeyRound,
  LoaderCircle,
  MessageSquareText,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Save,
  ShieldAlert,
  Workflow,
} from 'lucide-react'
import { useState, type ReactNode, type SetStateAction } from 'react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import { relativeTime } from '../../lib/format'
import type { TemplateProfile } from '../../types'
import { selectPreferredTemplateProfile } from './delivery-profile-selection'

const kitConfirmation = `Perfetto {{nome}}, grazie.

A breve ti assegneremo il migliore consulente.
Nei prossimi giorni riceverai una brevissima chiamata per fissare giorno e ora a te più comodi.

Nel frattempo, l’unica cosa che resta da fare è leggere il Kit: bastano cinque minuti ed è lì che trovi i numeri di cui parlerete.

Puoi aprire subito i materiali qui:

Guida PDF: https://www.pianofinanziariopem.it/guidapem
Calcolatore: https://www.pianofinanziariopem.it/calcolatore/

Te li abbiamo inviati anche via email, così potrai ritrovarli facilmente quando vuoi.

P.s. Se non vuoi aspettare la chiamata, scegli tu giorno e ora: {{link_prenotazione}}`

const kitPreview = `Ciao {{1}},

grazie per aver richiesto il Kit PEM gratuito.

Ecco i materiali:
Guida PDF: https://www.pianofinanziariopem.it/guidapem
Calcolatore: https://www.pianofinanziariopem.it/calcolatore/

Ne trovi una copia anche sulla mail.

Manca solo l’ultima parte: vogliamo assegnarti il consulente indipendente migliore che analizzerà gratuitamente il tuo portafoglio a schermo condiviso.

Per farlo basta sapere in quale fascia indicativa rientra:`

const callPreview = `Ciao, grazie per aver richiesto informazioni sul Piano Finanziario PEM.

Ti abbiamo inviato una mail con alcune informazioni aggiuntive.

A questo link puoi scaricare la guida PDF che ti consigliamo di visionare prima della call:
https://www.pianofinanziariopem.it/it/guidapem

Se non l'hai già fatto, a questo link puoi fissare la tua call gratuita:
https://www.pianofinanziariopem.it/prenota-call

Per qualsiasi informazione o domanda puoi scriverci direttamente qui su WhatsApp.

Saremo felici di aiutarti.`

const newProfile = (deliveryType: 'kit' | 'call' = 'kit'): TemplateProfile => ({
  id: '',
  key: deliveryType === 'kit' ? 'kit_delivery' : 'call_request',
  label: deliveryType === 'kit' ? 'Kit · fascia portafoglio' : 'Richiesta call',
  deliveryType,
  templateName: deliveryType === 'kit' ? 'kit_delivery' : 'call_request',
  languageCode: 'it',
  variableNames: deliveryType === 'kit' ? ['name'] : [],
  previewText: deliveryType === 'kit' ? kitPreview : callPreview,
  workflowCode: deliveryType === 'kit' ? 'kit_qualification_v1' : 'none',
  buttonMappings:
    deliveryType === 'kit'
      ? [
          { key: 'portfolio_lt_50k', label: 'meno di 50.000 €', value: 'lt_50k' },
          {
            key: 'portfolio_50k_250k',
            label: '50.000 - 250.000 €',
            value: '50k_250k',
          },
          { key: 'portfolio_gt_250k', label: 'oltre 250.000 €', value: 'gt_250k' },
        ]
      : [],
  confirmationText: deliveryType === 'kit' ? kitConfirmation : '',
  bookingUrl: '',
  headerMediaType: 'image',
  headerMediaUrl: 'https://pianofinanziariopem.it/assets/img/logo_rounded.png',
  active: false,
  providerButtons: [],
  createdAt: '',
  updatedAt: '',
})

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold">
      {label}
      {children}
      {hint && (
        <span className="text-xs font-normal leading-5 text-[var(--muted-foreground)]">{hint}</span>
      )}
    </label>
  )
}

const textareaClass =
  'min-h-28 w-full resize-y rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3.5 py-3 text-base leading-6 text-[var(--foreground)] shadow-sm outline-none transition placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:ring-3 focus:ring-[var(--primary-soft)]'

export function DeliverySettingsPanel() {
  const [credentialLabel, setCredentialLabel] = useState('Landing PEM produzione')
  const [generatedToken, setGeneratedToken] = useState('')
  const [profileDraft, setProfileDraft] = useState<TemplateProfile | null>(null)
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const settings = useQuery({ queryKey: ['delivery-settings'], queryFn: api.deliverySettings })
  const profiles = settings.data?.profiles ?? []
  const editingProfile = profileDraft ?? selectPreferredTemplateProfile(profiles, newProfile())
  const setEditingProfile = (action: SetStateAction<TemplateProfile>) => {
    setProfileDraft((current) => {
      const source = current ?? selectPreferredTemplateProfile(profiles, newProfile())
      return typeof action === 'function' ? action(source) : action
    })
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-settings'] })
    void queryClient.invalidateQueries({ queryKey: ['system-status'] })
  }

  const createCredential = useMutation({
    mutationFn: () => api.createDeliveryCredential(credentialLabel),
    onSuccess: (result) => {
      setGeneratedToken(result.token)
      showToast('Credenziale generata. Copiala ora: non sarà mostrata di nuovo.')
      refresh()
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Generazione fallita', 'error'),
  })
  const revokeCredential = useMutation({
    mutationFn: api.revokeDeliveryCredential,
    onSuccess: () => {
      showToast('Credenziale revocata')
      refresh()
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Revoca fallita', 'error'),
  })
  const setEnabled = useMutation({
    mutationFn: api.setDeliveryEnabled,
    onSuccess: (result) => {
      showToast(result.enabled ? 'API Landing attivata' : 'API Landing disattivata')
      refresh()
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Aggiornamento fallito', 'error'),
  })
  const saveProfile = useMutation({
    mutationFn: api.saveTemplateProfile,
    onSuccess: (profile) => {
      setEditingProfile(profile)
      showToast('Configurazione template salvata')
      refresh()
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Salvataggio fallito', 'error'),
  })
  const saveConfirmationText = useMutation({
    mutationFn: () =>
      api.saveTemplateConfirmationText(editingProfile.key, editingProfile.confirmationText),
    onSuccess: (profile) => {
      setEditingProfile(profile)
      showToast('Risposta al pulsante salvata')
      refresh()
    },
    onError: (error) =>
      showToast(
        error instanceof Error ? error.message : 'Salvataggio della risposta fallito',
        'error',
      ),
  })
  const verifyProfile = useMutation({
    mutationFn: api.verifyTemplateProfile,
    onSuccess: (profile) => {
      setEditingProfile(profile)
      showToast(
        profile.providerStatus === 'APPROVED'
          ? 'Template approvato e configurazione verificata'
          : `Template rilevato con stato ${profile.providerStatus ?? 'sconosciuto'}`,
        profile.providerStatus === 'APPROVED' && !profile.verificationError ? 'success' : 'error',
      )
      refresh()
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Verifica fallita', 'error')
      refresh()
    },
  })

  const copyToken = async () => {
    await navigator.clipboard.writeText(generatedToken)
    showToast('Token API copiato')
  }

  const updateType = (deliveryType: 'kit' | 'call') => {
    const defaults = newProfile(deliveryType)
    setEditingProfile((current) => ({
      ...current,
      deliveryType,
      workflowCode: defaults.workflowCode,
      buttonMappings: defaults.buttonMappings,
      confirmationText: defaults.confirmationText,
      previewText: current.previewText || defaults.previewText,
      bookingUrl: deliveryType === 'kit' ? current.bookingUrl : '',
      headerMediaType: current.headerMediaType || defaults.headerMediaType,
      headerMediaUrl: current.headerMediaUrl || defaults.headerMediaUrl,
      active: false,
    }))
  }

  const submitProfile = () => {
    if (!editingProfile.key || !editingProfile.label || !editingProfile.templateName) {
      showToast('Compila chiave, nome leggibile e nome template', 'error')
      return
    }
    saveProfile.mutate(editingProfile)
  }

  if (settings.isLoading) {
    return (
      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    )
  }

  const activeCredentials = settings.data?.credentials.filter((item) => item.active) ?? []
  const apiReady = activeCredentials.length > 0 && profiles.some((profile) => profile.active)

  return (
    <section
      aria-labelledby="delivery-settings-title"
      className="mt-14 border-t border-[var(--border)] pt-10"
    >
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--primary)]">
            Delivery e automazioni
          </p>
          <h2 id="delivery-settings-title" className="mt-2 text-2xl font-semibold tracking-tight">
            API per le landing
          </h2>
          <p className="mt-2 max-w-2xl text-base leading-7 text-[var(--muted-foreground)]">
            Configura credenziali e template approvati. Le landing scelgono il messaggio tramite una
            chiave pubblica, senza accedere a Dualhook.
          </p>
        </div>
        <Button
          variant={settings.data?.enabled ? 'danger' : 'primary'}
          disabled={setEnabled.isPending || (!settings.data?.enabled && !apiReady)}
          onClick={() => setEnabled.mutate(!settings.data?.enabled)}
        >
          {setEnabled.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Power className="size-4" />
          )}
          {settings.data?.enabled ? 'Disattiva API' : 'Attiva API'}
        </Button>
      </header>

      <div className="mt-8 grid items-start gap-5 lg:grid-cols-2">
        <Card className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
              <KeyRound className="size-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Credenziali server-to-server</h3>
              <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
                Il token va salvato nel backend della landing, mai nel browser.
              </p>
            </div>
          </div>

          {generatedToken && (
            <div className="mt-5 rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-[var(--warning)]" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[var(--warning)]">Copialo adesso</p>
                  <p className="mt-1 text-sm leading-5 text-[var(--muted-foreground)]">
                    Dopo aver lasciato questa pagina il valore completo non sarà recuperabile.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Input readOnly value={generatedToken} className="font-mono text-sm" />
                <Button
                  variant="secondary"
                  size="icon"
                  aria-label="Copia token"
                  onClick={copyToken}
                >
                  <Clipboard className="size-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Etichetta credenziale">
              <Input
                value={credentialLabel}
                onChange={(event) => setCredentialLabel(event.target.value)}
              />
            </Field>
            <Button
              className="sm:mb-0 sm:shrink-0"
              disabled={credentialLabel.trim().length < 3 || createCredential.isPending}
              onClick={() => createCredential.mutate()}
            >
              {createCredential.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Plus className="size-4" />
              )}
              Genera token
            </Button>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {settings.data?.credentials.length ? (
              settings.data.credentials.map((credential) => (
                <div
                  key={credential.id}
                  className="flex flex-col gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">{credential.label}</p>
                      <Badge>{credential.active ? 'Attiva' : 'Revocata'}</Badge>
                    </div>
                    <p className="mt-1 font-mono text-xs text-[var(--muted-foreground)]">
                      {credential.tokenPrefix}… · ultimo uso {relativeTime(credential.lastUsedAt)}
                    </p>
                  </div>
                  {credential.active && (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={revokeCredential.isPending}
                      onClick={() => revokeCredential.mutate(credential.id)}
                    >
                      Revoca
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-[var(--border)] p-5 text-center text-sm leading-6 text-[var(--muted-foreground)]">
                Nessuna credenziale. Generane una per collegare la prima landing.
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                <MessageSquareText className="size-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Profili disponibili</h3>
                <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
                  La chiave è il valore che verrà inviato dalla landing.
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditingProfile({ ...newProfile(), key: '' })}
            >
              <Plus className="size-4" /> Nuovo
            </Button>
          </div>

          <div className="mt-5 flex flex-col gap-3">
            {profiles.length ? (
              profiles.map((profile) => (
                <div
                  key={profile.key}
                  className="rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold">{profile.label}</p>
                        <Badge>{profile.deliveryType === 'kit' ? 'Kit' : 'Call'}</Badge>
                        {profile.active && (
                          <Badge className="bg-[var(--success-soft)] text-[var(--success)]">
                            Attivo
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 break-all font-mono text-xs text-[var(--muted-foreground)]">
                        {profile.key} · {profile.templateName} · {profile.languageCode}
                      </p>
                      <p className="mt-2 text-sm text-[var(--muted-foreground)]">
                        Meta: {profile.providerStatus ?? 'non verificato'}
                        {profile.lastVerifiedAt
                          ? ` · controllo ${relativeTime(profile.lastVerifiedAt)}`
                          : ''}
                      </p>
                      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                        Header Meta: {profile.providerHeaderFormat ?? 'nessuno rilevato'} · profilo:{' '}
                        {profile.headerMediaType === 'none'
                          ? 'nessun media'
                          : profile.headerMediaType}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingProfile(profile)}
                      >
                        <Pencil className="size-4" /> Modifica
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={verifyProfile.isPending}
                        onClick={() => verifyProfile.mutate(profile.key)}
                      >
                        <RefreshCw
                          className={`size-4 ${verifyProfile.isPending ? 'animate-spin' : ''}`}
                        />
                        Verifica
                      </Button>
                    </div>
                  </div>
                  {profile.verificationError && (
                    <p className="mt-3 rounded-lg bg-[var(--destructive-soft)] p-2.5 text-sm text-[var(--destructive)]">
                      {profile.verificationError}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-[var(--border)] p-5 text-center text-sm leading-6 text-[var(--muted-foreground)]">
                Crea i profili per il messaggio Kit e per la conferma Call.
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="mt-5 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
            <Workflow className="size-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              {editingProfile.id ? `Modifica ${editingProfile.key}` : 'Nuovo profilo template'}
            </h3>
            <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
              Template e risposta al pulsante hanno due salvataggi indipendenti.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Tipo consegna">
            <select
              value={editingProfile.deliveryType}
              disabled={Boolean(editingProfile.id)}
              onChange={(event) => updateType(event.target.value as 'kit' | 'call')}
              className="h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3.5 text-base outline-none focus:border-[var(--primary)] focus:ring-3 focus:ring-[var(--primary-soft)]"
            >
              <option value="kit">Kit</option>
              <option value="call">Call</option>
            </select>
          </Field>
          <Field label="Chiave pubblica" hint="Immutabile dopo il primo salvataggio.">
            <Input
              value={editingProfile.key}
              disabled={Boolean(editingProfile.id)}
              placeholder={editingProfile.deliveryType === 'kit' ? 'kit_delivery' : 'call_request'}
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, key: event.target.value }))
              }
            />
          </Field>
          <Field label="Nome leggibile">
            <Input
              value={editingProfile.label}
              placeholder="Kit · fascia portafoglio"
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, label: event.target.value }))
              }
            />
          </Field>
          <Field label="Nome template Meta">
            <Input
              value={editingProfile.templateName}
              placeholder={editingProfile.deliveryType === 'kit' ? 'kit_delivery' : 'call_request'}
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, templateName: event.target.value }))
              }
            />
          </Field>
          <Field label="Lingua">
            <Input
              value={editingProfile.languageCode}
              placeholder="it"
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, languageCode: event.target.value }))
              }
            />
          </Field>
          <Field label="Variabili" hint="Separate da virgola, nell’ordine del template.">
            <Input
              value={editingProfile.variableNames.join(', ')}
              placeholder="name"
              onChange={(event) =>
                setEditingProfile((current) => ({
                  ...current,
                  variableNames: event.target.value
                    .split(',')
                    .map((value) => value.trim())
                    .filter(Boolean),
                }))
              }
            />
          </Field>
          <Field label="Media header" hint="Deve corrispondere al formato del template approvato.">
            <select
              value={editingProfile.headerMediaType}
              onChange={(event) =>
                setEditingProfile((current) => ({
                  ...current,
                  headerMediaType: event.target.value as TemplateProfile['headerMediaType'],
                  headerMediaUrl: event.target.value === 'none' ? '' : current.headerMediaUrl,
                  active: false,
                }))
              }
              className="h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3.5 text-base outline-none focus:border-[var(--primary)] focus:ring-3 focus:ring-[var(--primary-soft)]"
            >
              <option value="none">Nessuno</option>
              <option value="image">Immagine</option>
              <option value="video">Video</option>
              <option value="document">Documento</option>
            </select>
          </Field>
          <Field
            label="URL media header"
            hint="URL HTTPS pubblico e stabile. Il file viene letto da Meta al momento dell’invio."
          >
            <Input
              type="url"
              value={editingProfile.headerMediaUrl}
              disabled={editingProfile.headerMediaType === 'none'}
              placeholder="https://.../header.png"
              onChange={(event) =>
                setEditingProfile((current) => ({
                  ...current,
                  headerMediaUrl: event.target.value,
                  active: false,
                }))
              }
            />
          </Field>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Field label="Anteprima nello storico" hint="Usa {{1}}, {{2}} secondo il template.">
            <textarea
              value={editingProfile.previewText}
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, previewText: event.target.value }))
              }
              className={textareaClass}
            />
          </Field>
          {editingProfile.deliveryType === 'kit' ? (
            <div className="flex flex-col gap-3">
              <Field
                label="Risposta al pulsante (testo libero)"
                hint="Non è un template Meta. Sono disponibili {{nome}} e {{link_prenotazione}}."
              >
                <textarea
                  value={editingProfile.confirmationText}
                  onChange={(event) =>
                    setEditingProfile((current) => ({
                      ...current,
                      confirmationText: event.target.value,
                    }))
                  }
                  className={textareaClass}
                />
              </Field>
              <Button
                variant="secondary"
                disabled={
                  !editingProfile.id ||
                  !editingProfile.confirmationText.trim() ||
                  saveConfirmationText.isPending
                }
                onClick={() => saveConfirmationText.mutate()}
              >
                {saveConfirmationText.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                Salva solo la risposta al pulsante
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-[var(--border)] p-5 text-sm leading-6 text-[var(--muted-foreground)]">
              Il profilo Call invia soltanto il template scelto dalla landing e non avvia il
              workflow di qualificazione Kit.
            </div>
          )}
        </div>

        {editingProfile.deliveryType === 'kit' && (
          <div className="mt-5 rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 sm:p-5">
            <h4 className="font-semibold">Corrispondenza dei pulsanti</h4>
            <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
              Copia esattamente le etichette del template approvato. PEMMY2 le correla al messaggio
              originale senza richiedere tag personalizzati.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {editingProfile.buttonMappings.map((mapping, index) => (
                <Field key={mapping.key} label={mapping.value}>
                  <Input
                    maxLength={20}
                    value={mapping.label}
                    onChange={(event) =>
                      setEditingProfile((current) => ({
                        ...current,
                        buttonMappings: current.buttonMappings.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, label: event.target.value } : item,
                        ),
                      }))
                    }
                  />
                </Field>
              ))}
            </div>
            <div className="mt-4">
              <Field label="Link prenotazione">
                <Input
                  type="url"
                  value={editingProfile.bookingUrl}
                  placeholder="https://..."
                  onChange={(event) =>
                    setEditingProfile((current) => ({
                      ...current,
                      bookingUrl: event.target.value,
                    }))
                  }
                />
              </Field>
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-3 text-sm font-semibold">
            <input
              type="checkbox"
              checked={editingProfile.active}
              disabled={
                editingProfile.providerStatus !== 'APPROVED' ||
                Boolean(editingProfile.verificationError)
              }
              onChange={(event) =>
                setEditingProfile((current) => ({ ...current, active: event.target.checked }))
              }
              className="size-5 accent-[var(--primary)]"
            />
            Profilo attivo e selezionabile dalle landing
          </label>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            {editingProfile.id && (
              <Button
                variant="secondary"
                disabled={verifyProfile.isPending || saveProfile.isPending}
                onClick={() => verifyProfile.mutate(editingProfile.key)}
              >
                {verifyProfile.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                Verifica con Dualhook
              </Button>
            )}
            <Button disabled={saveProfile.isPending} onClick={submitProfile}>
              {saveProfile.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : editingProfile.active ? (
                <CheckCircle2 className="size-4" />
              ) : (
                <Save className="size-4" />
              )}
              Salva configurazione template
            </Button>
          </div>
        </div>
      </Card>
    </section>
  )
}
